import { useEffect, useState } from "react";

/**
 * ScopeLockRender — instant scope-lock preview.
 *
 * Listens for `window.dispatchEvent(new CustomEvent("mf:scope-lock", ...))`
 * fired by DynamicMaterialsBuilder when the owner taps a variation
 * card. Zero-friction hands-free rendering per master pipeline
 * directive Feb 12, 2026.
 *
 * Two render modes:
 *   • variant="inline" (default) — mounted inside Cockpit; full
 *     dual-panel SVG + line-item render.
 *   • variant="floating" — mounted at App root; compact fixed-position
 *     overlay that surfaces on ANY page (Design Studio, Dashboard,
 *     Field Mode, /expo) the moment a variation is selected. Includes
 *     a "→ Open Cockpit" jump so the owner can pop into the full
 *     inline render without hunting for the tab.
 *
 * Both variants share the same sessionStorage key so an inline mount
 * hydrates from the last floating capture and vice-versa.
 */

const SCOPE_LOCK_KEY = "mf_scope_lock_current";

const useScopeLock = () => {
  const [lock, setLock] = useState(() => {
    try {
      const stored = sessionStorage.getItem(SCOPE_LOCK_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  useEffect(() => {
    const handler = (e) => {
      const payload = e.detail || null;
      setLock(payload);
      try {
        if (payload) sessionStorage.setItem(SCOPE_LOCK_KEY, JSON.stringify(payload));
        else sessionStorage.removeItem(SCOPE_LOCK_KEY);
      } catch { /* ignore */ }
    };
    window.addEventListener("mf:scope-lock", handler);

    // Cross-tab sync — if another tab locks a variation the storage
    // event fires here so this tab's floating overlay updates too.
    const onStorage = (e) => {
      if (e.key !== SCOPE_LOCK_KEY) return;
      try { setLock(e.newValue ? JSON.parse(e.newValue) : null); } catch { /* ignore */ }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("mf:scope-lock", handler);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const clear = () => {
    setLock(null);
    try { sessionStorage.removeItem(SCOPE_LOCK_KEY); } catch { /* ignore */ }
  };

  return [lock, clear];
};

const ScopeLockRender = ({ variant = "inline" }) => {
  const [lock, clearLock] = useScopeLock();
  const [collapsed, setCollapsed] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);

  if (!lock) return null;

  if (variant === "floating") {
    return (
      <div data-testid="scope-lock-floating" style={floatingOuter}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <div style={pulseDot} />
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.16em", fontWeight: 800 }}>
                &gt; SCOPE LOCK · LIVE
              </div>
              <div style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>
                {lock.variation_id} · {lock.stream_label}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 4, flex: "0 0 auto" }}>
            <button
              data-testid="scope-lock-floating-toggle"
              onClick={() => setCollapsed((v) => !v)}
              style={pillBtn}
              aria-label={collapsed ? "expand" : "collapse"}
            >
              {collapsed ? "▾" : "▴"}
            </button>
            <button
              data-testid="scope-lock-floating-clear"
              onClick={clearLock}
              style={pillBtn}
              aria-label="clear scope lock"
            >
              ✕
            </button>
          </div>
        </div>
        {!collapsed && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <div style={{ color: "#7E92A8", fontSize: "0.66rem", letterSpacing: "0.14em", fontWeight: 700 }}>
                LINE TOTAL
              </div>
              <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.95rem", fontWeight: 800 }}>
                ${(lock.line_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            <svg width="100%" height="80" viewBox="0 0 260 90" preserveAspectRatio="xMidYMid meet" style={{ background: "rgba(0,0,0,0.28)", borderRadius: 4 }}>
              <rect x="14" y="12" width="232" height="66" fill="rgba(0,220,120,0.08)" stroke="#00DC78" strokeWidth="1.2" rx="3" />
              <line x1="110" y1="12" x2="150" y2="12" stroke="#FFB347" strokeWidth="2.5" />
              <line x1="40" y1="78" x2="80" y2="78" stroke="#5AC8D8" strokeWidth="1.8" />
              <line x1="180" y1="78" x2="220" y2="78" stroke="#5AC8D8" strokeWidth="1.8" />
              <text x="130" y="50" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="9" fill="#7E92A8">
                AUTO-COMPILED
              </text>
            </svg>
            {lock.acclamation && (
              <div style={{ color: "#FFB347", fontSize: "0.62rem", marginTop: 6, fontStyle: "italic", lineHeight: 1.4 }}>
                {lock.acclamation}
              </div>
            )}
            <button
              data-testid="scope-lock-floating-open-cockpit"
              onClick={() => { window.location.assign("/dashboard"); }}
              style={openCockpitBtn}
            >
              → Open in Cockpit
            </button>
          </div>
        )}
      </div>
    );
  }

  // ─── Inline (default, Cockpit) ──────────────────────────────
  const entries = Object.entries(lock.categories || {});
  return (
    <div data-testid="scope-lock-render" style={inlineOuter}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={eyebrow}>&gt; SCOPE LOCK · INLINE RENDER · HANDS-FREE</div>
          <div style={{ color: "#fff", fontSize: "1.05rem", fontWeight: 800, marginTop: 3 }}>
            {lock.variation_id} · {lock.stream_label}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", fontSize: "1.1rem", fontWeight: 800 }}>
            ${(lock.line_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
          <button
            data-testid="scope-lock-clear"
            onClick={clearLock}
            style={{ background: "transparent", color: "#7E92A8", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 4, padding: "3px 8px", fontSize: "0.7rem", cursor: "pointer" }}>
            clear
          </button>
        </div>
      </div>

      <div style={grid}>
        <svg width="100%" height="180" viewBox="0 0 320 180" preserveAspectRatio="xMidYMid meet" style={svgWrap}>
          <rect x="20" y="20" width="280" height="140" fill="rgba(0,220,120,0.06)" stroke="#00DC78" strokeWidth="1.5" rx="4" />
          <line x1="140" y1="20" x2="180" y2="20" stroke="#FFB347" strokeWidth="3" />
          <line x1="60" y1="160" x2="110" y2="160" stroke="#5AC8D8" strokeWidth="2" />
          <line x1="210" y1="160" x2="260" y2="160" stroke="#5AC8D8" strokeWidth="2" />
          <rect x="34" y="34" width="46" height="20" fill="#A78BFA" opacity="0.55" />
          <rect x="240" y="34" width="46" height="20" fill="#A78BFA" opacity="0.55" />
          <circle cx="160" cy="90" r="14" fill="none" stroke="#E8B8C4" strokeWidth="1.5" opacity="0.7" />
          <text x="160" y="152" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#7E92A8">
            AUTO-COMPILED · {lock.variation_id}
          </text>
        </svg>

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {entries.slice(0, 6).map(([k, meta]) => (
            <div key={k} style={lineRow}>
              <div style={{ color: "#B4BEC8", fontSize: "0.75rem", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {k}
              </div>
              <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem", flex: "0 0 auto" }}>
                {meta.quantity}× · ${meta.subtotal || (meta.quantity * meta.unit_price).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {lock.acclamation && (
        <div style={{ color: "#FFB347", fontSize: "0.68rem", marginTop: 10, fontStyle: "italic", lineHeight: 1.45 }}>
          {lock.acclamation}
        </div>
      )}
      <div style={{ color: "#7E92A8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", marginTop: 4, letterSpacing: "0.14em" }}>
        NSPIRE compliant · HUD-VASH ready · 48-hr turn standard auto-enforced
      </div>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────
const inlineOuter = {
  background: "linear-gradient(180deg, rgba(0,220,120,0.06) 0%, rgba(0,255,255,0.04) 100%)",
  border: "1px solid rgba(0,220,120,0.32)",
  borderRadius: 12,
  padding: "14px 16px 16px",
};
const eyebrow = {
  color: "#00DC78", fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.66rem", letterSpacing: "0.2em", fontWeight: 800,
};
const grid = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  gap: 12,
  alignItems: "start",
};
const svgWrap = { background: "rgba(0,0,0,0.25)", borderRadius: 6 };
const lineRow = {
  display: "flex", alignItems: "center",
  padding: "6px 10px", background: "rgba(0,0,0,0.28)",
  border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4,
};

const floatingOuter = {
  position: "fixed",
  right: 16,
  bottom: 88, // sits above the mobile FAB / bottom nav
  width: 300,
  maxWidth: "calc(100vw - 32px)",
  background: "linear-gradient(180deg, rgba(3,11,26,0.98) 0%, rgba(6,20,38,0.98) 100%)",
  border: "1px solid rgba(0,220,120,0.45)",
  borderRadius: 10,
  padding: "10px 12px",
  boxShadow: "0 12px 32px rgba(0,0,0,0.55), 0 0 22px rgba(0,220,120,0.18)",
  zIndex: 9600,
  backdropFilter: "blur(6px)",
};
const pulseDot = {
  width: 8, height: 8, borderRadius: "50%",
  background: "#00DC78",
  boxShadow: "0 0 10px #00DC78",
  flex: "0 0 auto",
};
const pillBtn = {
  background: "transparent",
  color: "#7E92A8",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 4,
  padding: "3px 8px",
  fontSize: "0.7rem",
  cursor: "pointer",
  lineHeight: 1,
};
const openCockpitBtn = {
  marginTop: 10,
  width: "100%",
  background: "linear-gradient(90deg, #00DC78, #00B364)",
  color: "#03151F",
  border: "none",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: "0.75rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  cursor: "pointer",
};

export default ScopeLockRender;
